import Big from 'big.js'
import useTranslation from 'next-translate/useTranslation'
import { useContext, useEffect, useMemo, useState } from 'react'

import useRefreshedRate from '../hooks/useRefreshedRate'

import CryptoCounter from './CryptoCounter'
import FiatCounter from './FiatCounter'
import { MetronomeContext } from './MetronomeContext'
import OperationUnitInput from './OperationUnitInput'
import TokenSelector from './TokenSelector'

const BorrowSynth = function ({
  availableDebtSupply = 0,
  availableSynthSupply = 0,
  borrowed,
  debtTokenTotalSupplyExceeded,
  disabled,
  leverage,
  onChange,
  selectedSynthetic,
  setBorrowed,
  syntheticTotalSupplyExceeded
}) {
  const { t } = useTranslation()

  const {
    inputValue,
    selectedPoolData: { swapRoutes, synthetics },
    selectedToken
  } = useContext(MetronomeContext)

  const [borrowedAmount, setBorrowedAmount] = useState(0)

  const [rate] = useRefreshedRate({
    tokenA: selectedToken,
    tokenB: selectedSynthetic
  })

  const availableSynthetics = useMemo(
    () =>
      swapRoutes.leverageRoutes
        .filter(
          route =>
            selectedToken?.mainCollateral?.address === route.tokenIn.address
        )
        .map(function (route) {
          const synthetic = synthetics.find(
            synth => synth.address === route.synth
          )
          return synthetic ? synthetic : undefined
        })
        .filter(s => !!s),
    [
      selectedToken?.mainCollateral?.address,
      swapRoutes.leverageRoutes,
      synthetics
    ]
  )

  const borrowConverted = useMemo(
    () => (rate ? Big(borrowedAmount).mul(rate) : Big(0)),
    [borrowedAmount, rate]
  )

  useEffect(
    function () {
      onChange?.(selectedSynthetic)
    },
    [onChange, selectedSynthetic]
  )

  useEffect(
    function () {
      setBorrowed(Big(borrowConverted).lte(0) ? Big(0) : borrowConverted)
    },
    [borrowConverted, setBorrowed]
  )

  useEffect(
    function () {
      setBorrowedAmount(Big(leverage).minus(1).times(inputValue).toFixed())
    },
    [inputValue, leverage]
  )

  const supplyExceeded =
    syntheticTotalSupplyExceeded || debtTokenTotalSupplyExceeded

  return (
    <>
      <span className="text-lg font-medium">{t('borrow-synth')}</span>
      <div className="bg-blue-5 text-gray-4 border-blue-5 my-6 rounded-lg border px-4 pb-3 pt-4">
        <div className="flex justify-between">
          <div>
            <OperationUnitInput
              balancePrefix={t('operation-modal.deposit.balance-prefix')}
              disableInputs={true}
              disabled={true}
              focusOnTokenChange={false}
              initialValue={0}
              inputProps={{ name: 'synth-input' }}
              selectedToken={selectedSynthetic}
              setValue={setBorrowed}
              value={borrowed}
            />
          </div>
          <div className="relative z-10 font-sans">
            <TokenSelector
              availableTokens={availableSynthetics}
              chooseLabel={t('choose-synth')}
              disabled={disabled}
              displayBalances={false}
              setToken={onChange}
              token={selectedSynthetic}
            />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-sm">
          <FiatCounter end={borrowConverted.toFixed()} prefix="≈$" />
          <div className={` ${supplyExceeded ? 'text-red-600' : ''}`}>
            <CryptoCounter
              end={Math.min(availableSynthSupply, availableDebtSupply)}
              prefix={`${t('operation-modal.generate.balance-prefix')}: `}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default BorrowSynth
